import { isErrorResponse } from '@bulknutrients/bulk-shared-lib/interfaces/BulkAPI';
import type { CreateAccountFormFields } from '@bulknutrients/bulk-shared-lib/interfaces/IForms';
import { getErrorMessage } from '@bulknutrients/bulk-shared-lib/utils';
import { ErrorAlert } from '@components/Alerts/ErrorAlert';
import { SuccessAlert } from '@components/Alerts/SuccessAlert';
import { Button, ButtonType } from '@components/Button/Button';
import {
  CreateAccountFields,
  SignupErrorMessage,
} from '@components/CreateAccountForm/CreateAccountForm';
import { Input } from '@components/FormComponents/FormComponents';
import { Modal } from '@components/Modal/Modal';
import { Paragraph } from '@components/Typography/Paragraph/Paragraph';
import { TextLink } from '@components/Typography/TextLink/TextLink';
import { useCustomer } from '@hooks/customer/useCustomer';
import { useSignup } from '@hooks/customer/useSignup';
import { useKlaviyo } from '@hooks/useKlaviyo';
import {
  type ReactElement,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useFormContext } from 'react-hook-form';

const recaptchaCheck = async (recaptchaValue: string | null) => {
  if (!recaptchaValue) {
    return false;
  }

  const captchaCheckResponse = await fetch('/api/recaptcha', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ code: recaptchaValue as string, version: 'v2' }),
  });

  const captchaCheck = await captchaCheckResponse.json();

  if (isErrorResponse(captchaCheck)) {
    return false;
  }

  return captchaCheck.data;
};

export function SignUpModal({
  showModal,
  setShowModal,
  leadCopy,
  successMessage,
}: {
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  leadCopy: ReactNode;
  successMessage: ReactNode;
}): ReactElement {
  const rhfMethods = useFormContext<CreateAccountFormFields>();
  const signup = useSignup();
  const { customer } = useCustomer();
  const { newsletterSubscribe, smsSubscribe } = useKlaviyo();
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const [success, setSuccess] = useState(false);
  const [localSuccessMessage, setLocalSuccessMessage] =
    useState(successMessage);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<ReactNode>(
    <p>
      Something unforeseen has happened there, sorry! Please try again or{' '}
      <TextLink href="/contact-us">contact us</TextLink>.
    </p>
  );
  const [submitting, setSubmitting] = useState(false);

  const handleSignup = async (formData: CreateAccountFormFields) => {
    setSubmitting(true);

    // Check the Captcha is correct
    const captchaCheck = recaptchaRef.current
      ? await recaptchaCheck(recaptchaRef.current.getValue())
      : false;
    if (!captchaCheck) {
      setError(true);
      setErrorMessage('Please complete the captcha correctly');
      setSubmitting(false);
      return;
    }

    try {
      // Signup in BC
      const placeholderPassword = `${Math.random().toString(36).substring(2, 15)}aA1`;
      const customer = await signup({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        password: placeholderPassword,
        phone: formData.phone,
        addresses: [
          {
            first_name: formData.firstName,
            last_name: formData.lastName,
            address1: formData.address1,
            city: formData.suburb,
            state_or_province: formData.state,
            postal_code: formData.postcode,
            country_code: formData.country,
            address_type: 'residential',
          },
        ],
        generatedPassword: true,
      });

      if (isErrorResponse(customer)) {
        throw Error(`signup${customer.error.message.join('. ')}`);
      }

      // Klaviyo Subscriptions
      const klaviyoPromises = [] as Array<Promise<boolean>>;
      if (formData.newsletter) {
        // If the customer has signed up for the newsletter then we can add them to Klaviyo
        klaviyoPromises.push(
          newsletterSubscribe({
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
          })
        );
        klaviyoPromises.push(
          smsSubscribe({
            email: formData.email,
            phone: formData.phone,
            firstName: formData.firstName,
            lastName: formData.lastName,
          })
        );
      }
      const klaviyoSubscribeResponses = await Promise.all(klaviyoPromises);
      if (klaviyoSubscribeResponses.some((response) => !response)) {
        setLocalSuccessMessage(
          <div>
            <Paragraph>
              Your account has been created and you should receive an email to
              set your password. In the meantime, you can now request your
              sample 🥳
            </Paragraph>
            <Paragraph>
              Unfortunately, we failed to subscribe you to our newsletter or sms
              list. You can opt into emails and text messages from your{' '}
              <TextLink href="/account/settings">account settings</TextLink>, or
              reach out to our{' '}
              <TextLink href="/contact-us">customer service team</TextLink> to
              get you signed up.
            </Paragraph>
          </div>
        );
      }
      setSuccess(true);
      rhfMethods.reset();
      setSubmitting(false);
    } catch (err) {
      const message = getErrorMessage(err);
      if (message.startsWith('signup')) {
        setErrorMessage(<SignupErrorMessage msg={message.slice(6)} />);
      } else {
        setErrorMessage(
          <div>
            <Paragraph>
              Sorry, something went wrong with the last step there! You{' '}
              <i>should</i> have an account now, but please{' '}
              <TextLink href="/contact-us">
                reach out to our customer service team
              </TextLink>{' '}
              if you need help (like no email received) or to double check!
            </Paragraph>
            <Paragraph>
              You can also tell them the error you saw was:{' '}
              <pre>{errorMessage}</pre>
            </Paragraph>
          </div>
        );
      }
      setError(true);
      setSubmitting(false);
      recaptchaRef.current?.reset();
      return;
    }
  };

  useEffect(() => {
    if (customer) {
      setShowModal(false);
    }
  }, [customer, setShowModal]);

  return (
    <>
      <ErrorAlert show={error} setShow={setError}>
        {errorMessage}
      </ErrorAlert>
      <SuccessAlert show={success} setShow={setSuccess}>
        {localSuccessMessage}
      </SuccessAlert>

      <Modal
        show={showModal}
        setShow={setShowModal}
        className={
          'data-[state=open]:animate-contentShow max-h-screen w-[90vw] max-w-[600px] p-[25px] shadow-[hsl(206_22%_7%/35%)_0px_10px_38px_-10px,hsl(206_22%_7%/20%)_0px_10px_20px_-15px] focus:outline-hidden overflow-y-auto'
        }
      >
        <section className="max-w-[500px]">
          <h2 className="py-4">Create an account</h2>
          <Paragraph>{leadCopy}</Paragraph>
          <form onSubmit={rhfMethods.handleSubmit(handleSignup)}>
            <CreateAccountFields methods={rhfMethods} includeAddress={true} />
            <div className="my-4">
              <ReCAPTCHA
                ref={recaptchaRef}
                sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
              />
            </div>
            <Input
              type="checkbox"
              label="Send me exclusive news & offers"
              name="newsletter"
              value="newsletter"
              required
              description={
                <>
                  I understand that by submitting this form, I consent to
                  receive email and sms communications from Bulk Nutrients and
                  agree to the{' '}
                  <TextLink
                    href="/bulk-nutrients-terms-conditions"
                    target="_blank"
                  >
                    T&Cs
                  </TextLink>{' '}
                  and{' '}
                  <TextLink
                    href="/bulk-nutrients-privacy-policy"
                    target="_blank"
                  >
                    Privacy Policy
                  </TextLink>
                  .
                </>
              }
            />

            <div className="btn-create-account">
              <Button
                type={ButtonType.submit}
                disabled={submitting || !rhfMethods.formState.isValid}
                buttonStyle="primary"
              >
                Create Account
              </Button>
            </div>
          </form>
        </section>
      </Modal>
    </>
  );
}
